<template>
  <section class="ssm-hero">
    <h1 class="visually-hidden">
      {{ title }}
    </h1>
    <div class="ssm-hero__logo">
      <div class="ssm-hero__particles">
        <PortalParticles />
      </div>
      <SsmLogo />
      <!-- Twice the poster's size, shown at the logo's scale (2/3 of the poster). -->
      <PixelText
        class="ssm-hero__subtitle"
        :text="title"
        :scale="8"
        smooth
      />
      <SplashText class="ssm-hero__splash" />
    </div>
  </section>
  <ClockTimeline class="mc-panel" />
  <div class="button-container">
    <router-link
      v-if="!$store.state.archived"
      class="button button-primary register"
      :to="{ name: 'signin' }"
    >
      Register!
    </router-link>
  </div>
  <div class="content">
    <div class="mc-panel">
      <h3>Description</h3>
      <p>
        Säkerhets-SM is a national Capture The Flag competition for high
        schools students. The competition focuses on problem solving in
        the areas of programming, cryptography, binary exploitation,
        reverse engineering, web security and forensics. The tasks range
        from beginner-friendly to really tricky, so even if you've never
        participated in a CTF before, you can take part. Above all, you
        will learn a lot from participating.
      </p>
      <p>
        CTF (Capture The Flag) competitions involve getting access to a
        set of challenges at the start of the competition and
        demonstrating that you have solved a task by submitting the
        task's "flag" to the platform. Flags in this competition are
        text strings of the form <code>SSM{...}</code>.
      </p>
      <p>
        This year's competition consists of a qualifying round and a
        final. The qualifying round takes place online, here, from
        Friday December 4th 20:00 CET to Sunday December 6th 20:00 CET
        2026. The competition thus lasts for 48 hours, but it is
        already possible to register your team!
      </p>
      <p>
        Some of the challenges in the qualifying round are marked as easy. We recommend you to start with them if you haven't played
        a CTF competition before! We also allow you to ask the
        organizers for clues on these easy challenges, so don't hesitate
        to send a message to the challenge author via
        <a href="https://discord.gg/m6pcEYp">Discord</a> or post a
        message in #säkerhetssm if you get stuck!
      </p>
      <h3>Final</h3>
      <p>
        The best teams qualify for the final, which this year is held in
        Stockholm! It takes place February 18th-21st 2027, and we
        pay for any travel (up to a certin limit TBD), accommodation and
        food costs. The qualification cutoff will be decided after the
        qualification round has concluded.
      </p>
      <h3>Discord</h3>
      <p>
        Join Kodsport's
        <a href="https://discord.gg/m6pcEYp">Discord server</a> to discuss
        the competition with others in the #säkerhetssm channel! Remember
        that it's not allowed to discuss the problems while the competition
        is ongoing! If you don't have any teammates or if your team isn't
        full, you can also send a message in the #leta-lag channel to find
        people to play with. In the Discord, you'll also find all the
        organizers, admins, and challenge authors in case you need to
        contact them; they have the role @Säkerhets-SM.
      </p>
    </div>
    <div class="mc-panel">
      <h3>Rules</h3>
      <ul>
        <li>
          Using machine learning tools or LLMs during the competition
          is strictly forbidden. This includes AI tools such as
          ChatGPT, Claude, Gemini, Google AI Overviews and GitHub
          Copilot.
        </li>
        <li>
          For a team to compete officially, it must meet these base
          requirements:
          <ul>
            <li>All team members are born in 2008 or later.</li>
            <li>The team has at most 3 members.</li>
          </ul>
          The team must also meet one of the following requirements,
          which decides its competition class:
          <ul>
            <li>
              Sweden class: All team members are registered residents
              (folkbokförda) of Sweden.
            </li>
            <li>
              Nordic class: All team members are registered residents
              of the same one of these countries: Denmark, Finland,
              Iceland or Norway. The team then represents that country.
            </li>
          </ul>
        </li>
        <li>
          If any of the requirements above are not met, you can still
          take part in the competition, but outside of the official
          ranking.
        </li>
        <li>
          It is forbidden to be a member of multiple teams
          simultaneously.
        </li>
        <li>
          The 48 participants from the highest-scoring teams in the
          Sweden class qualify for the final, rounded down to whole
          teams. This number may change.
        </li>
        <li>
          The highest-scoring team from each country in the Nordic
          class qualifies for the final.
        </li>
        <li>
          The 4 participants with the highest scores in the ICO
          selection who are eligible to represent Sweden under the ICO
          rules qualify for ICO 2027.
        </li>
        <li>
          The 20 participants from the highest-scoring teams in the
          Sweden class who have not already qualified for the SCSC
          final qualify for it, rounded down to whole teams.
        </li>
        <li>
          Unless explicitly stated otherwise, it is forbidden to
          discuss solutions and hints with anyone outside your team
          while the competition is ongoing.
        </li>
        <li>
          You may search the internet, read discussions on forums and
          use previously written code, but you may not ask questions
          yourself during the competition. For example, it is not
          allowed to ask about the challenges on Discord.
        </li>
        <li>
          For challenges marked as easy, you may ask the organizers for
          hints.
        </li>
        <li>
          It is forbidden to attack the CTF platform. This includes
          brute-forcing flags against the competition platform.
        </li>
        <li>
          Respect the challenge servers. Do not create unreasonably
          large amounts of traffic to the challenges. No challenge
          requires sending an unreasonable amount of data to get the
          flag.
          <div class="tooltip">
            ?<span class="tooltiptext">What counts as an unreasonable amount of data?<br><br>If
              you see a solution to a task that requires a few
              thousand requests, this is completely okay to
              perform. If your solution requires tens or hundreds
              of thousands of requests, your solution is probably
              wrong, and you should find a better solution to not
              affect the server's performance while solving. It is
              not okay to blindly spam the server with data, for
              example, through a dumb scanner like dirbuster,
              unless it is stated in the task that it is
              allowed.</span>
          </div>
        </li>
        <li>
          It is forbidden to disrupt other people's ability to solve
          challenges.
        </li>
        <li>
          It is forbidden to have more than one account on the CTF
          platform.
        </li>
        <li>
          It is forbidden to use the platform for commercial marketing.
        </li>
        <li>
          Breaking any of these rules may get individual participants
          or whole teams disqualified.
        </li>
        <li>
          This is a competition in ethical hacking, take
          responsibility!
        </li>
        <li>The decisions of the organizers are final.</li>
      </ul>
      <p>
        At the final, recording your screen for the entire competition
        is mandatory. Finalists will be told how to do this.
      </p>
    </div>
  </div>
  <div class="mc-panel sponsors">
    <h3>I samarbete med</h3>
    <div class="mainsponsorcontainer">
      <a href="https://www.fra.se/">
        <img
          class="mainsponsor"
          src="@/assets/sponsors/fra.png"
          alt="FRA - Försvarets Radioanstalt"
        >
      </a>
    </div>
    <p style="text-align: center">
      Kodsport's infrastructure is sponsored by
      <a href="https://glesys.com/">GleSYS</a>
    </p>
  </div>
  <p style="text-align: center">
    <a href="/privacy-policy.html">Privacy Policy</a>
  </p>
</template>

<script>
import ClockTimeline from '@/components/ClockTimeline.vue'
import PixelText from '@/components/PixelText.vue'
import PortalParticles from '@/components/PortalParticles.vue'
import SplashText from '@/components/SplashText.vue'
import SsmLogo from '@/components/SsmLogo.vue'

export default {
    name: 'HomeView',
    components: {
        ClockTimeline,
        PixelText,
        PortalParticles,
        SplashText,
        SsmLogo,
    },
    data () {
        return {
            // The rainbow title under the logo.
            title: 'Säkerhets-SM 2027 Quals',
        }
    },
}
</script>

<style lang="scss" scoped>
.ssm-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 2rem;
    text-align: center;
}

.ssm-hero__logo {
    position: relative;
    width: min(664px, 100%);
    margin-top: 1rem;
}

.ssm-hero__particles {
    position: absolute;
    inset: -12% -8%;
    pointer-events: none;
}

// Where the poster puts it: under the S's, starting at the first one.
.ssm-hero__subtitle {
    position: absolute;
    left: 4.42%;
    top: 73.25%;
    width: 68%;
}

.ssm-hero__splash {
    position: absolute;
    top: 6%;
    right: -6%;
}

h3 {
    text-align: center;
}

p {
    padding: 0 10px 0 10px;
}

.button-container {
    margin-top: 2rem;
    text-align: center;
}

.register {
    display: inline-block;
    padding: 0.6rem 1.5rem;
    font-size: 1.6875rem;
}

.content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
    padding-top: 3rem;
    margin-bottom: 1.5rem;
}

.sponsors {
    margin-bottom: 1.5rem;
}

.mainsponsorcontainer {
    text-align: center;
    margin-top: 1rem;
}

.mainsponsor {
    max-width: 100%;
    max-height: 350px;
    padding-bottom: 1rem;
}

.sponsorcontainer {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.sponsor {
    max-width: 90%;
    max-height: 100px;
    margin: 1rem 1rem;
}

@media only screen and (max-width: 990px) {
    .content {
        grid-template-columns: minmax(0, 1fr);
    }

    .ssm-hero__splash {
        right: -2%;
    }
}
</style>
